import { useEffect, useRef, useState } from "react";
import { AlertTriangle, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { TRUTH_ACK_EVENT } from "./truthAck";
import { categoryLabel } from "./truthLabels";

const MAX_SHOWN = 5;

// Warn (pilot) mode: a flagged note can still be signed once the clinician writes why it's correct.
export function TruthAckDialog() {
  const [req, setReq] = useState(null);
  const [reason, setReason] = useState("");
  const pending = useRef(null);

  useEffect(() => {
    const onAsk = (e) => {
      pending.current?.(null);
      pending.current = e.detail.resolve;
      setReason("");
      setReq(e.detail);
    };
    window.addEventListener(TRUTH_ACK_EVENT, onAsk);
    return () => window.removeEventListener(TRUTH_ACK_EVENT, onAsk);
  }, []);

  const finish = (value) => {
    pending.current?.(value);
    pending.current = null;
    setReq(null);
  };

  const min = req?.min_chars || 10;
  const flags = req?.blockers || [];
  const ok = reason.trim().length >= min;
  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && finish(null)}>
      <DialogContent className="sm:max-w-lg z-[110]" overlayClassName="z-[105]" data-testid="truth-ack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-amber-600" /> Sign with a reason</DialogTitle>
          <DialogDescription>{req?.message}</DialogDescription>
        </DialogHeader>
        {flags.length > 0 && (
          <ul className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30 p-3" data-testid="truth-ack-flags">
            {flags.slice(0, MAX_SHOWN).map((f, i) => (
              <li key={`${f.code}-${i}`} className="flex gap-2 text-xs text-amber-900 dark:text-amber-200" data-testid={`truth-ack-flag-${i}`}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><span className="font-semibold">{categoryLabel(f.code)}:</span> {f.message}</span>
              </li>
            ))}
            {flags.length > MAX_SHOWN && <li className="text-xs text-amber-800">and {flags.length - MAX_SHOWN} more</li>}
          </ul>
        )}
        <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} rows={4} autoFocus
          placeholder="For example: I assessed this at the bedside today; the weight is from the scale reading I took."
          data-testid="truth-ack-reason-input" />
        <p className="text-[11px] text-slate-500">
          {ok ? "Saved with the note's audit record." : `At least ${min} characters.`}
        </p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => finish(null)} data-testid="truth-ack-cancel-btn">Cancel</Button>
          <Button onClick={() => finish(reason.trim())} disabled={!ok} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="truth-ack-submit-btn">
            Sign with this reason
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
